import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import classNames from 'classnames';
import { EdmundsLogo } from 'site-modules/shared/components/edmunds-logo/edmunds-logo';
import { Link } from 'site-modules/shared/components/link/link';
import { AnimatedCollapse } from 'site-modules/shared/utils/collapse-utils';
import { OKTA_DEALER_PORTAL_URL } from 'site-modules/shared/constants/dealer-urls';

import './press-header.scss';

const DEALER_SUPPORT_URL = 'https://edmundsdealersupport.zendesk.com/hc/en-us';
const HELP_CENTER_URL = 'https://help.edmunds.com/hc/en-us';
const CONTACT_US_URL = 'https://help.edmunds.com/hc/en-us/requests/new';

const NAV_LINKS = [
  { id: 'press', label: 'Press', href: '/press/' },
  {
    id: 'insights',
    label: 'Insights',
    href: '/insights/',
  },
  {
    id: 'products',
    label: 'Products',
    href: 'https://dealers.edmunds.com/products/',
    columns: [
      {
        heading: 'Products',
        items: [
          { label: 'Connected Video', href: 'https://dealers.edmunds.com/products/connected-video' },
          { label: 'Instant Cash Offer', href: 'https://dealers.edmunds.com/products/instant-cash-offer' },
          { label: 'Advertising Solutions', href: 'https://dealers.edmunds.com/products/ad-solutions' },
          { label: 'Trade-In Tool', href: 'https://dealers.edmunds.com/products/trade-in-tool' },
          { label: 'CarCode Messaging', href: 'https://dealers.edmunds.com/products/carcode' },
          { label: 'Edmunds Service Drive', href: 'https://dealers.edmunds.com/products/service-drive' },
        ],
      },
      {
        heading: 'Solutions',
        items: [
          { label: 'Premier', href: 'https://dealers.edmunds.com/products/premier' },
          { label: 'Premier (as seen on CDG)', href: 'https://dealers.edmunds.com/cdg' },
          { label: 'Used+ Car Dealers', href: '/industry/used-car-dealers.html' },
          { label: 'OEMs', href: '/industry/oems.html' },
        ],
      },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    href: CONTACT_US_URL,
    items: [
      { label: 'Contact Us', href: CONTACT_US_URL },
      { label: 'Help Center', href: HELP_CENTER_URL },
      { label: 'Dealer Support', href: DEALER_SUPPORT_URL },
    ],
  },
];

const CONTACT_ITEMS = [
  { label: 'General Inquiries', href: CONTACT_US_URL },
  { label: 'Press Inquiries', href: 'mailto:pr@edmunds.com' },
  { label: 'Dealer Support', href: DEALER_SUPPORT_URL },
];

const CONTACT_ID = 'contact';

function NavButton({ id, label, openedId, onToggle }) {
  const handleClick = useCallback(() => onToggle(id), [id, onToggle]);
  return (
    <button
      type="button"
      className={classNames('press-header__nav-link size-16 bg-transparent border-0 p-0', {
        'press-header__nav-link--active': openedId === id,
      })}
      onClick={handleClick}
      aria-expanded={openedId === id}
      data-tracking-id="press_header_nav"
      data-tracking-value={label}
    >
      {label}
    </button>
  );
}

NavButton.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  openedId: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
};

function MobileNavItem({ navLink, onClose }) {
  const { label, href, items, columns } = navLink;

  if (!items && !columns) {
    return (
      <li className="press-header__mobile-item">
        <Link
          to={href}
          className="press-header__mobile-link size-20 text-decoration-none d-flex align-items-center"
          data-tracking-id="press_header_nav"
          data-tracking-value={label}
          onClick={onClose}
        >
          {label}
        </Link>
        <hr className="press-header__mobile-divider m-0" />
      </li>
    );
  }

  const subItems = items || columns.flatMap(({ items: colItems }) => colItems);

  return (
    <li className="press-header__mobile-item">
      <div className="press-header__mobile-link size-20 d-flex align-items-center">{label}</div>
      <ul className="press-header__mobile-subitems list-unstyled m-0">
        {subItems.map(({ label: subLabel, href: subHref }) => (
          <li key={subLabel}>
            <Link
              to={subHref}
              className="press-header__mobile-sublink size-16 text-decoration-none d-block"
              data-tracking-id="press_header_submenu"
              data-tracking-value={subLabel}
              onClick={onClose}
            >
              {subLabel}
            </Link>
          </li>
        ))}
      </ul>
      <hr className="press-header__mobile-divider m-0" />
    </li>
  );
}

MobileNavItem.propTypes = {
  navLink: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, href: PropTypes.string })),
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        heading: PropTypes.string,
        items: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, href: PropTypes.string })),
      })
    ),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export function PressHeader() {
  const [openedId, setOpenedId] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleEntry = useCallback(id => setOpenedId(prev => (prev === id ? '' : id)), []);
  const closeAll = useCallback(() => setOpenedId(''), []);
  const toggleContact = useCallback(() => toggleEntry(CONTACT_ID), [toggleEntry]);

  const toggleMobile = useCallback(() => setMobileOpen(prev => !prev), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const openedNav = NAV_LINKS.find(({ id }) => id === openedId);
  const isContactOpen = openedId === CONTACT_ID;

  return (
    <>
      <header className="press-header bg-white" data-tracking-parent="edm-entry-press-header">
        <Container className="press-header__inner d-flex align-items-center justify-content-between py-1_5">
          <EdmundsLogo path="/" trackingId="press_header_logo" />

          {/* Desktop nav */}
          <nav className="press-header__nav d-none d-lg-flex align-items-center gap-2" aria-label="Press navigation">
            {NAV_LINKS.map(({ id, label, href, items, columns }) =>
              items || columns ? (
                <NavButton key={id} id={id} label={label} openedId={openedId} onToggle={toggleEntry} />
              ) : (
                <Link
                  key={id}
                  to={href}
                  className="press-header__nav-link size-16 text-decoration-none"
                  data-tracking-id="press_header_nav"
                  data-tracking-value={label}
                  onClick={closeAll}
                >
                  {label}
                </Link>
              )
            )}
          </nav>

          <div className="press-header__actions d-flex align-items-center gap-2">
            {/* Desktop-only actions */}
            <button
              type="button"
              className={classNames('press-header__nav-link size-16 bg-transparent border-0 p-0 d-none d-lg-block', {
                'press-header__nav-link--active': isContactOpen,
              })}
              onClick={toggleContact}
              aria-expanded={isContactOpen}
              data-tracking-id="press_header_contact"
            >
              Contact Us
            </button>
            <Link
              aria-label="Sign In"
              to={OKTA_DEALER_PORTAL_URL}
              className="press-header__sign-in size-16 text-decoration-none d-none d-lg-flex align-items-center gap-0_25"
              data-tracking-id="press_header_sign_in"
              onClick={closeAll}
            >
              <span className="icon-user3 size-20" aria-hidden />
              <span className="icon-arrow-down3 size-10" aria-hidden />
            </Link>

            {/* Mobile hamburger */}
            <button
              type="button"
              className={classNames('press-header__hamburger d-lg-none bg-transparent border-0 p-0', {
                'press-header__hamburger--open': mobileOpen,
              })}
              onClick={toggleMobile}
              aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileOpen}
              data-tracking-id="press_header_hamburger"
            >
              <span className="icon-menu7" aria-hidden />
            </button>
          </div>
        </Container>

        {/* Desktop submenus */}
        <AnimatedCollapse className="press-header__submenu" isOpen={!!(openedNav?.items || openedNav?.columns)}>
          {openedNav?.columns ? (
            <Container>
              <div className="d-flex justify-content-center py-1_5">
                {openedNav.columns.map(({ heading, items }) => (
                  <div key={heading} className="press-header__submenu-column">
                    <div className="press-header__submenu-heading size-16 mb-1_5">{heading}</div>
                    <ul className="list-unstyled m-0 d-flex flex-column gap-1_5">
                      {items.map(({ label, href }) => (
                        <li key={label}>
                          <Link
                            to={href}
                            className="press-header__submenu-link size-16 text-decoration-none"
                            data-tracking-id="press_header_submenu"
                            data-tracking-value={label}
                            onClick={closeAll}
                          >
                            {label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </Container>
          ) : (
            openedNav?.items && (
              <Container>
                <ul className="press-header__submenu-list list-unstyled m-0 d-flex justify-content-center gap-2 py-1_5">
                  {openedNav.items.map(({ label, href }) => (
                    <li key={label}>
                      <Link
                        to={href}
                        className="press-header__submenu-link size-16 text-decoration-none"
                        data-tracking-id="press_header_submenu"
                        data-tracking-value={label}
                        onClick={closeAll}
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </Container>
            )
          )}
        </AnimatedCollapse>

        <AnimatedCollapse className="press-header__submenu" isOpen={isContactOpen}>
          <Container>
            <ul className="press-header__submenu-list list-unstyled m-0 d-flex justify-content-center gap-2 py-1_5">
              {CONTACT_ITEMS.map(({ label, href }) => (
                <li key={label}>
                  <Link
                    to={href}
                    className="press-header__submenu-link size-16 text-decoration-none"
                    data-tracking-id="press_header_contact_submenu"
                    data-tracking-value={label}
                    onClick={closeAll}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </Container>
        </AnimatedCollapse>

        {/* Mobile menu */}
        <AnimatedCollapse isOpen={mobileOpen} className="press-header__mobile-overlay d-lg-none" overflowOnExpanded>
          <nav className="press-header__mobile-menu" aria-label="Press navigation">
            <Container>
              <ul className="list-unstyled m-0">
                {NAV_LINKS.map(navLink => (
                  <MobileNavItem key={navLink.id} navLink={navLink} onClose={closeMobile} />
                ))}

                {/* Contact Us */}
                <li className="press-header__mobile-item">
                  <div className="press-header__mobile-link size-20 d-flex align-items-center">Contact Us</div>
                  <ul className="press-header__mobile-subitems list-unstyled m-0">
                    {CONTACT_ITEMS.map(({ label, href }) => (
                      <li key={label}>
                        <Link
                          to={href}
                          className="press-header__mobile-sublink size-16 text-decoration-none d-block"
                          data-tracking-id="press_header_contact_submenu"
                          data-tracking-value={label}
                          onClick={closeMobile}
                        >
                          {label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <hr className="press-header__mobile-divider m-0" />
                </li>

                {/* Sign In */}
                <li className="press-header__mobile-item">
                  <Link
                    to={OKTA_DEALER_PORTAL_URL}
                    className="press-header__mobile-link size-20 text-decoration-none d-flex align-items-center gap-0_5"
                    data-tracking-id="press_header_sign_in"
                    onClick={closeMobile}
                  >
                    <span className="icon-user3 size-20" aria-hidden />
                    Sign In
                  </Link>
                </li>
              </ul>
            </Container>
          </nav>
        </AnimatedCollapse>
      </header>

      {/* Desktop backdrop */}
      {!!openedId && (
        <button
          type="button"
          className="press-header__backdrop d-none d-lg-block"
          onClick={closeAll}
          aria-label="Close navigation menu"
        />
      )}
    </>
  );
}
